import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, ListGroup, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { fetchLookups, getDisplayValue } from '../../utils/lookupUtils';

const MessageList = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [lookups, setLookups] = useState({});

  // Fetch lookups for message types
  useEffect(() => {
    const getLookups = async () => {
      try {
        const lookupData = await fetchLookups(null, null);
        console.log('Fetched lookups for MessageList:', lookupData);
        setLookups(lookupData);
      } catch (error) {
        console.error('Error fetching lookups:', error);
      }
    };

    getLookups();
  }, []);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch conversations from the API
        const response = await api.get('/conversations');

        if (response.data?.conversations) {
          console.log('Fetched conversations:', response.data.conversations);

          // Log the structure of the first conversation if available
          if (response.data.conversations.length > 0) {
            const firstConv = response.data.conversations[0];
            console.log('First conversation structure:', {
              id: firstConv.id,
              users: firstConv.users,
              message: firstConv.message,
              links: firstConv._links,
              unread: firstConv.unread_count
            });
          }

          setConversations(response.data.conversations);
        } else {
          console.warn('No conversations found in API response');
          setConversations([]);
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
        setError('Failed to load conversations. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();

    // Get date parts for comparison (year, month, day)
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Calculate the difference in days between the dates
    const diffTime = todayDate - messageDate;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      // Today - show time
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      // Yesterday
      return 'Yesterday';
    } else if (diffDays < 7) {
      // Within a week - show day name
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      // Older - show date
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const filteredConversations = conversations.filter(conversation => {
    if (activeTab === 'all') return true;
    if (activeTab === 'unread') return conversation.unread_count > 0;
    if (activeTab === 'offers') {
      // Check if message_type is related to offers (offer: 2, offer_accept: 3, offer_deny: 4, offer_cancel: 5,
      // offer_request: 6, offer_request_accept: 7, offer_request_deny: 8, offer_request_cancel: 9)
      const messageType = conversation.message?.message_type;
      return messageType === 2 || messageType === '2' || messageType === 'offer' ||
             messageType === 3 || messageType === '3' || messageType === 'offer_accept' ||
             messageType === 4 || messageType === '4' || messageType === 'offer_deny' ||
             messageType === 5 || messageType === '5' || messageType === 'offer_cancel' ||
             messageType === 6 || messageType === '6' || messageType === 'offer_request' ||
             messageType === 7 || messageType === '7' || messageType === 'offer_request_accept' ||
             messageType === 8 || messageType === '8' || messageType === 'offer_request_deny' ||
             messageType === 9 || messageType === '9' || messageType === 'offer_request_cancel';
    }
    return true;
  });

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading conversations...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h2 className="fw-bold mb-4">Messages</h2>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={4} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom-0 pt-3 pb-0">
              <ul className="nav nav-tabs card-header-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                  >
                    All
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'unread' ? 'active' : ''}`}
                    onClick={() => setActiveTab('unread')}
                  >
                    Unread
                    {conversations.reduce((count, conv) => count + conv.unread_count, 0) > 0 && (
                      <Badge bg="danger" className="ms-2">
                        {conversations.reduce((count, conv) => count + conv.unread_count, 0)}
                      </Badge>
                    )}
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'offers' ? 'active' : ''}`}
                    onClick={() => setActiveTab('offers')}
                  >
                    Offers
                  </button>
                </li>
              </ul>
            </Card.Header>
            <Card.Body className="p-0">
              {filteredConversations.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-chat-left-text display-4 text-muted"></i>
                  <p className="mt-3 text-muted">No conversations found</p>
                </div>
              ) : (
                <ListGroup variant="flush" className="conversation-list">
                  {filteredConversations.map(conversation => {
                    // Get the other user from the users array (first element)
                    const otherUser = conversation.users && conversation.users.length > 0
                      ? conversation.users[0]
                      : null;

                    // If no user in the users array, try to extract from _links
                    let otherUsername = otherUser?.username;

                    // Fallback to extracting from _links if users array is empty or username is missing
                    if (!otherUsername && conversation._links?.users) {
                      const userLink = conversation._links.users.find(link => {
                        const username = link.href.split('/').pop();
                        return username !== (conversation.current_user_username || '');
                      });

                      if (userLink) {
                        otherUsername = userLink.href.split('/').pop();
                      }
                    }

                    // Final fallback
                    if (!otherUsername) {
                      otherUsername = 'Unknown';
                    }

                    console.log('Conversation user:', otherUser);

                    return (
                      <ListGroup.Item
                        key={conversation.id}
                        action
                        as={Link}
                        to={`/messages/${otherUsername}`}
                        className={`py-3 px-3 d-flex align-items-center ${conversation.unread_count > 0 ? 'bg-light' : ''}`}
                      >
                        <div className="position-relative me-3">
                          {otherUser?.avatars?.small ? (
                            <img
                              src={otherUser.avatars.small}
                              alt={otherUsername}
                              className="avatar-small"
                            />
                          ) : (
                            <div className="avatar-small d-flex align-items-center justify-content-center bg-light rounded-circle">
                              <i className="bi bi-person-fill text-secondary"></i>
                            </div>
                          )}
                          {otherUser?.online && (
                            <span className="position-absolute bottom-0 end-0 p-1 bg-success rounded-circle">
                              <span className="visually-hidden">Online</span>
                            </span>
                          )}
                        </div>

                        <div className="flex-grow-1 min-width-0">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <h6 className="mb-0 text-truncate">
                              {otherUsername}
                              <Link
                                to={`/users/${otherUsername}`}
                                onClick={(e) => e.stopPropagation()}
                                className="ms-2 text-primary"
                                title="View Profile"
                              >
                                <i className="bi bi-person-badge"></i>
                              </Link>
                            </h6>
                            <small className="text-muted ms-2">
                              {conversation.message?.created_dt ? formatDate(conversation.message.created_dt) : ''}
                            </small>
                          </div>

                          <div className="d-flex justify-content-between align-items-center">
                            <p className="mb-0 text-truncate text-muted small">
                              {(conversation.message?.message_type === 2 ||
                                conversation.message?.message_type === '2' ||
                                conversation.message?.message_type === 'offer') && (
                                <Badge bg="info" className="me-1">
                                  {getDisplayValue(lookups, 'message_type', 2, 'Offer')}
                                </Badge>
                              )}
                              {(conversation.message?.message_type === 3 ||
                                conversation.message?.message_type === '3' ||
                                conversation.message?.message_type === 'offer_accept') && (
                                <Badge bg="success" className="me-1">
                                  {getDisplayValue(lookups, 'message_type', 3, 'Offer Accepted')}
                                </Badge>
                              )}
                              {(conversation.message?.message_type === 4 ||
                                conversation.message?.message_type === '4' ||
                                conversation.message?.message_type === 'offer_deny') && (
                                <Badge bg="warning" className="me-1">
                                  {getDisplayValue(lookups, 'message_type', 4, 'Offer Denied')}
                                </Badge>
                              )}
                              {(conversation.message?.message_type === 5 ||
                                conversation.message?.message_type === '5' ||
                                conversation.message?.message_type === 'offer_cancel') && (
                                <Badge bg="danger" className="me-1">
                                  {getDisplayValue(lookups, 'message_type', 5, 'Offer Canceled')}
                                </Badge>
                              )}
                              {(conversation.message?.message_type === 6 ||
                                conversation.message?.message_type === '6' ||
                                conversation.message?.message_type === 'offer_request') && (
                                <Badge bg="primary" className="me-1">
                                  {getDisplayValue(lookups, 'message_type', 6, 'Date Request')}
                                </Badge>
                              )}
                              {(conversation.message?.message_type === 7 ||
                                conversation.message?.message_type === '7' ||
                                conversation.message?.message_type === 'offer_request_accept') && (
                                <Badge bg="success" className="me-1">
                                  {getDisplayValue(lookups, 'message_type', 7, 'Request Accepted')}
                                </Badge>
                              )}
                              {(conversation.message?.message_type === 8 ||
                                conversation.message?.message_type === '8' ||
                                conversation.message?.message_type === 'offer_request_deny') && (
                                <Badge bg="warning" className="me-1">
                                  {getDisplayValue(lookups, 'message_type', 8, 'Request Denied')}
                                </Badge>
                              )}
                              {(conversation.message?.message_type === 9 ||
                                conversation.message?.message_type === '9' ||
                                conversation.message?.message_type === 'offer_request_cancel') && (
                                <Badge bg="danger" className="me-1">
                                  {getDisplayValue(lookups, 'message_type', 9, 'Request Canceled')}
                                </Badge>
                              )}
                              {conversation.message?.message}
                            </p>

                            {conversation.unread_count > 0 && (
                              <Badge bg="danger" pill className="ms-2">
                                {conversation.unread_count}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center p-5">
              <i className="bi bi-chat-square-text display-1 text-muted mb-4"></i>
              <h4 className="fw-bold mb-3">Select a Conversation</h4>
              <p className="text-muted text-center mb-4">
                Choose a conversation from the list to view messages or start a new conversation.
              </p>
              <Button as={Link} to="/search/results" variant="primary">
                Find People to Message
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MessageList;
